/**
 * @file Testimonials.tsx
 * @description Client testimonials carousel with navigation dots.
 * 
 * Features:
 * - Auto-sliding testimonials (can be enabled)
 * - Manual navigation via dots
 * - Client name, role, location, and rating display
 * - Star rating visualization
 * 
 * @dependencies None (pure React component)
 */
import { useState } from 'react';

/**
 * Testimonials carousel displaying client reviews and ratings.
 * Uses dot navigation for manual slide control.
 */
export default function Testimonials() {
  const [activeIndex, setActiveIndex] = useState(0);

  const testimonials = [
    {
      name: 'Parimal Rajesh',
      role: 'Design Engineer',
      location: 'Bangalore, Karnataka',
      text: 'Best financial advisor to work with comprehensive financial plans. Keeping me focused on long term investment and guiding me feel secure about my future. I took secure insurance plan for my girl baby it was satisfying me, and wife feel good lessening to their financial advice.',
      rating: 5,
    },
    {
      name: 'Krishna Kanth Koratala',
      role: 'Business Owner',
      location: 'Andhra Pradesh',
      text: 'I contact Mr. K Satish for a health insurance plan, and he suggested me a good plan in Care insurance. After few weeks he helped me in claim also. I am appreciating his help in tough times. Thank you, Mr. K Satish.',
      rating: 5,
    },
    {
      name: 'Dr. Kousalya Devi',
      role: 'Medical Professional',
      location: 'Tamil Nadu',
      text: 'They are highly knowledgeable, transparent about fees, and truly personalized my retirement plan. I appreciate that they are accessible and proactive, keeping me calm and focused on long-term goals during market dips. Their guidance has been invaluable in organizing my finances and helping me feel secure about my future.',
      rating: 5,
    },
    {
      name: 'Bandu Naveen',
      role: 'Software Engineer',
      location: 'Suryapet, Telangana',
      text: 'I wanted to take a moment to sincerely thank you for your guidance and support throughout the process of selecting and finalizing my term insurance plan. Your clear explanations, patience in addressing my questions, and ability to recommend the right policy based on my needs made the entire experience smooth and reassuring. I especially appreciate the way you handled the end-to-end execution, ensuring that every step was completed efficiently and without any hassle on my part.',
      rating: 5,
    },
    {
      name: 'Rajesh Kumar',
      role: 'Software Engineer',
      location: 'Bangalore',
      text: 'I was confused about which insurance to buy. The advisor patiently explained everything and helped me choose the right term plan. Very transparent and professional!',
      rating: 5,
    },
    {
      name: 'Priya Sharma',
      role: 'Marketing Manager',
      location: 'Mumbai',
      text: 'Got my entire family covered with health insurance within a week. The claims process was smooth too. Highly recommend for personalized advice!',
      rating: 5,
    },
    {
      name: 'Amit Patel',
      role: 'Business Owner',
      location: 'Ahmedabad',
      text: 'Been working with them for 5+ years. From insurance to mutual funds, the guidance has been excellent. They genuinely care about clients.',
      rating: 5,
    },
    {
      name: 'Sneha Reddy',
      role: 'Teacher',
      location: 'Hyderabad',
      text: 'Started my SIP journey with very small amounts. Today I have a good portfolio. The advisor reviews it regularly and suggests changes. Very satisfied!',
      rating: 5,
    },
    {
      name: 'Vikram Singh',
      role: 'Doctor',
      location: 'Delhi',
      text: 'Professional, knowledgeable, and always available. Helped me plan for my child\'s education and our retirement. Worth every consultation!',
      rating: 5,
    },
  ];

  const nextTestimonial = () => {
    setActiveIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setActiveIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  return (
    <section id="testimonials" className="py-16 md:py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            What Our <span className="text-blue-600">Clients Say</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Real experiences from families we've helped secure their financial future
          </p>
        </div>

        {/* Main Testimonial Card */}
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12 relative overflow-hidden">
            {/* Quote Icon */}
            <div className="absolute top-0 right-0 text-blue-100 opacity-50">
              <svg className="w-32 h-32" fill="currentColor" viewBox="0 0 24 24">
                <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
              </svg>
            </div>

            <div className="relative z-10">
              {/* Rating */}
              <div className="flex mb-4">
                {[...Array(testimonials[activeIndex].rating)].map((_, i) => (
                  <svg
                    key={i}
                    className="w-6 h-6 text-yellow-400"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>

              {/* Testimonial Text */}
              <p className="text-xl md:text-2xl text-gray-700 mb-8 leading-relaxed">
                "{testimonials[activeIndex].text}"
              </p>

              {/* Author Info */}
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-400 to-green-400 rounded-full flex items-center justify-center text-white text-2xl font-bold shadow-lg">
                  {testimonials[activeIndex].name.charAt(0)}
                </div>
                <div>
                  <div className="font-bold text-gray-900 text-lg">
                    {testimonials[activeIndex].name}
                  </div>
                  <div className="text-gray-600 text-sm">
                    {testimonials[activeIndex].role} | {testimonials[activeIndex].location}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex justify-center items-center space-x-4 mt-8">
            <button
              onClick={prevTestimonial}
              className="p-3 rounded-full bg-white shadow-lg hover:bg-gray-50 transition-colors"
              aria-label="Previous testimonial"
            >
              <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            {/* Dots */}
            <div className="flex space-x-2">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setActiveIndex(index)}
                  className={`w-3 h-3 rounded-full transition-all ${
                    index === activeIndex ? 'bg-blue-600 w-8' : 'bg-gray-300'
                  }`}
                  aria-label={`Go to testimonial ${index + 1}`}
                />
              ))}
            </div>

            <button
              onClick={nextTestimonial}
              className="p-3 rounded-full bg-white shadow-lg hover:bg-gray-50 transition-colors"
              aria-label="Next testimonial"
            >
              <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="mt-16 grid grid-cols-3 gap-8 max-w-3xl mx-auto">
          <div className="text-center">
            <div className="text-3xl md:text-4xl font-bold text-blue-600 mb-2">4.9/5</div>
            <div className="text-gray-600 text-sm">Average Rating</div>
          </div>
          <div className="text-center">
            <div className="text-3xl md:text-4xl font-bold text-green-600 mb-2">1000+</div>
            <div className="text-gray-600 text-sm">Happy Clients</div>
          </div>
          <div className="text-center">
            <div className="text-3xl md:text-4xl font-bold text-blue-600 mb-2">100%</div>
            <div className="text-gray-600 text-sm">Satisfaction</div>
          </div>
        </div>
      </div>
    </section>
  );
}
